import { GameVars } from "../../../GameVars";
import { BoardPhase } from "../../../Types";
import { BoardManager, BoardPhaseSnapshot, BoardPlaySnapshot } from "../BoardManager";
import { buildHUDViewState } from "../BoardViewState";
import { ContractBid } from "../bidding-menu/ContractBid";

export class ContractStatusPanel extends Phaser.GameObjects.Container {

    private static readonly WIDTH = 450;
    private static readonly MARGIN = 16;
    private static readonly DISPLAY_SCALE = .7;
    private static readonly TRICKS_OFFSET_X = 65;
    private static readonly TRICKS_OFFSET_Y = -10;
    private static readonly SEATS = ["N", "E", "S", "W"];

    private readonly declarerLabel: Phaser.GameObjects.Text;
    private readonly dealerLabel: Phaser.GameObjects.Text;
    private readonly seatBackgrounds: Phaser.GameObjects.Graphics;
    private readonly tricksContainer: Phaser.GameObjects.Container;
    private readonly trickMarkers: Phaser.GameObjects.Graphics;
    private readonly trickCounter: Phaser.GameObjects.Text;
    private readonly unsubscribeFromPhase: () => void;
    private readonly unsubscribeFromPlay: () => void;
    private completedTricks = 0;
    private contractBid?: ContractBid;

    constructor(scene: Phaser.Scene, private readonly boardManager: BoardManager) {

        super(scene);
        this.setVisible(false).setAlpha(0);

        const separators = new Phaser.GameObjects.Graphics(scene);
        separators.lineStyle(2, 0x08618c, .65);
        [185, 310].forEach(x => separators.lineBetween(x, 42, x, 120));
        this.add(separators);

        this.addLabel(100, 16, "CONTRACT", 32, "#ffda72");
        this.addLabel(245, 16, "DECL.", 32, "#c8eaf5");
        this.addLabel(365, 16, "DEALER", 32, "#ffda72");

        this.seatBackgrounds = new Phaser.GameObjects.Graphics(scene);
        this.seatBackgrounds.fillStyle(0x001b39, .6);
        [245, 365].forEach(x => this.seatBackgrounds.fillCircle(x, 85, 28));
        this.seatBackgrounds.setVisible(false);
        this.add(this.seatBackgrounds);
        this.declarerLabel = this.addLabel(245, 54, "", 46, "#c8eaf5");
        this.dealerLabel = this.addLabel(365, 54, "", 46, "#c8eaf5");

        this.tricksContainer = new Phaser.GameObjects.Container(scene);
        this.add(this.tricksContainer);
        this.addLabel(32, 16, "TRICKS", 25, "#ffffff", this.tricksContainer).setOrigin(0, 0);
        this.trickMarkers = new Phaser.GameObjects.Graphics(scene);
        this.tricksContainer.add(this.trickMarkers);
        this.drawTrickMarkers();
        this.trickCounter = this.addLabel(431, 40, "0 / 13", 24, "#ffffff", this.tricksContainer);

        this.relayout();
        this.unsubscribeFromPhase = boardManager.subscribeToPhase(this.onPhaseChanged.bind(this));
        this.unsubscribeFromPlay = boardManager.subscribeToPlay(this.onPlayStateChanged.bind(this));
    }

    public relayout(): void {

        const scale = ContractStatusPanel.DISPLAY_SCALE * Math.min(1, (GameVars.gameWidth - ContractStatusPanel.MARGIN * 2)
            / ContractStatusPanel.WIDTH);
        this.setPosition(ContractStatusPanel.MARGIN, ContractStatusPanel.MARGIN * GameVars.scaleY - 5);
        this.setScale(scale, scale * GameVars.scaleY);
        const tricksScale = Math.min(1, (GameVars.gameWidth / 2 - ContractStatusPanel.MARGIN * 2)
            / (ContractStatusPanel.WIDTH * scale));
        this.tricksContainer.setScale(tricksScale);
        this.tricksContainer.setPosition(
            (GameVars.gameWidth * .75 + ContractStatusPanel.TRICKS_OFFSET_X - this.x) / scale
            - ContractStatusPanel.WIDTH * tricksScale / 2,
            ContractStatusPanel.TRICKS_OFFSET_Y / this.scaleY
        );
    }

    public destroy(fromScene?: boolean): void {

        this.unsubscribeFromPhase();
        this.unsubscribeFromPlay();
        this.scene.tweens.killTweensOf(this);
        super.destroy(fromScene);
    }

    private onPlayStateChanged(snapshot: BoardPlaySnapshot): void {

        const viewState = buildHUDViewState(snapshot.state, snapshot.contractResult?.outcome);
        if (snapshot.state && this.contractBid) {
            this.contractBid.setOutcome(viewState.contractOutcome);
        }
        const completed = viewState.completedTricks;
        if (completed === this.completedTricks) {
            return;
        }
        this.completedTricks = completed;
        this.trickCounter.setText(`${completed} / 13`);
        this.drawTrickMarkers();
    }

    private drawTrickMarkers(): void {

        this.trickMarkers.clear();
        this.trickMarkers.lineStyle(2, 0x08618c, 1);
        for (let trick = 0; trick < 13; trick++) {
            const x = 42 + trick * 27;
            this.trickMarkers.fillStyle(trick < this.completedTricks ? 0xffd45b : 0x001b39, 1);
            this.trickMarkers.fillCircle(x, 57, 10);
            this.trickMarkers.strokeCircle(x, 57, 10);
        }
    }

    private onPhaseChanged(snapshot: BoardPhaseSnapshot): void {

        this.contractBid?.destroy();
        this.contractBid = undefined;
        const bidding = snapshot.phase === BoardPhase.BIDDING;
        const contract = bidding ? null : snapshot.contract;
        this.declarerLabel.setText(contract ? ContractStatusPanel.SEATS[contract.declarer] : "");
        this.dealerLabel.setText(bidding ? ""
            : ContractStatusPanel.SEATS[this.boardManager.getState().deal.dealer]);
        this.seatBackgrounds.setVisible(!bidding);

        if (contract) {
            this.contractBid = new ContractBid(this.scene, contract, { width: 140, height: 64 });
            this.contractBid.setPosition(100, 85);
            this.add(this.contractBid);
        }

        if (snapshot.phase !== BoardPhase.PLAY && snapshot.phase !== BoardPhase.RESULT) {
            this.scene.tweens.killTweensOf(this);
            this.setVisible(false).setAlpha(0);
        } else if (!this.visible) {
            this.setVisible(true);
            this.scene.tweens.add({
                targets: this,
                alpha: 1,
                duration: 450,
                ease: Phaser.Math.Easing.Cubic.Out
            });
        }
    }

    private addLabel(
        x: number, y: number, text: string, fontSize: number, color: string,
        parent: Phaser.GameObjects.Container = this
    ): Phaser.GameObjects.Text {

        const label = new Phaser.GameObjects.Text(this.scene, x, y, text, {
            fontFamily: "Baloo2-SemiBold",
            fontSize: `${fontSize}px`,
            color,
            stroke: "#001124",
            strokeThickness: 2
        }).setOrigin(.5, 0);
        parent.add(label);
        return label;
    }
}
